import { useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { AlertTriangle, ArrowUpDown, ChevronDown, ChevronRight, Layers, Plus, RefreshCw, Save, Sparkles, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  IconButton,
  Input,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTable,
} from '@/components/ui'
import type { AccountMappingRow, BwaPosition, CoaAccount } from './types.ts'

// ─── Kontenbaum-Logik ────────────────────────────────────────────────────────

interface TreeNode {
  account: CoaAccount
  children: TreeNode[]
}

function buildFatherBasedTree(accounts: CoaAccount[]): TreeNode[] {
  const byCode = new Map(accounts.map((a) => [a.code, a]))
  const childrenMap = new Map<string, CoaAccount[]>()

  for (const a of accounts) {
    const fatherCode = a.father ?? ''
    const parentExists = fatherCode && byCode.has(fatherCode)
    const key = parentExists ? fatherCode : '__root__'
    if (!childrenMap.has(key)) childrenMap.set(key, [])
    childrenMap.get(key)!.push(a)
  }

  function buildNodes(parentKey: string): TreeNode[] {
    return (childrenMap.get(parentKey) ?? [])
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((a) => ({ account: a, children: buildNodes(a.code) }))
  }

  return buildNodes('__root__')
}

function buildLevelBasedTree(accounts: CoaAccount[]): TreeNode[] {
  const sorted = [...accounts].sort((a, b) => a.code.localeCompare(b.code))
  const roots: TreeNode[] = []
  const stack: Array<{ node: TreeNode; level: number }> = []

  for (const account of sorted) {
    const level = account.level ?? 0
    const node: TreeNode = { account, children: [] }

    while (stack.length > 0 && stack[stack.length - 1]!.level >= level) stack.pop()

    if (stack.length === 0) roots.push(node)
    else stack[stack.length - 1]!.node.children.push(node)

    if (account.isGroup) stack.push({ node, level })
  }
  return roots
}

function buildTree(accounts: CoaAccount[]): { nodes: TreeNode[]; usedLevelFallback: boolean } {
  const fatherTree = buildFatherBasedTree(accounts)
  const rootRatio = accounts.length > 0 ? fatherTree.length / accounts.length : 1

  if (rootRatio < 0.75) return { nodes: fatherTree, usedLevelFallback: false }

  const hasLevels = accounts.some((a) => (a.level ?? 0) > 0)
  if (hasLevels) return { nodes: buildLevelBasedTree(accounts), usedLevelFallback: true }

  // Kein Father-Feld, kein Level → AccountType-Einteilung als Fallback
  return { nodes: buildAccountTypeTree(fatherTree), usedLevelFallback: false }
}

const ACCOUNT_TYPE_META: Record<string, { label: string; order: number }> = {
  // SAP B1 Service Layer (neue Enum-Werte)
  at_Revenue:          { label: 'Ertragskonten',           order: 1 },
  at_OtherRevenue:     { label: 'Sonstige Ertragskonten',  order: 2 },
  at_Expenditure:      { label: 'Aufwandskonten',           order: 3 },
  at_OtherExpenditure: { label: 'Sonstiger Aufwand',        order: 4 },
  at_Assets:           { label: 'Aktivkonten (Bilanz)',      order: 5 },
  at_Liabilities:      { label: 'Passivkonten (Bilanz)',     order: 6 },
  at_Equity:           { label: 'Eigenkapitalkonten',        order: 7 },
  // Aeltere / alternative SAP B1 Enum-Werte
  at_Revenues:         { label: 'Ertragskonten',            order: 1 },
  at_Expenses:         { label: 'Aufwandskonten',           order: 3 },
  at_Other:            { label: 'Sonstige Konten (Bilanz)', order: 5 },
}

function buildAccountTypeTree(flatNodes: TreeNode[]): TreeNode[] {
  const postingNodes = flatNodes.filter((n) => !n.account.isGroup)
  const groupNodes   = flatNodes.filter((n) => n.account.isGroup)

  const byType = new Map<string, TreeNode[]>()
  for (const node of postingNodes) {
    const key = node.account.accountType ?? '__unknown__'
    if (!byType.has(key)) byType.set(key, [])
    byType.get(key)!.push(node)
  }

  const buckets: TreeNode[] = []
  for (const [type, nodes] of byType) {
    const meta = ACCOUNT_TYPE_META[type]
    const label = meta ? meta.label : type
    buckets.push({
      account: { code: `__type__${type}`, name: `${label} (${nodes.length})`, isGroup: true, father: '', level: 0 },
      children: nodes.sort((a, b) => a.account.code.localeCompare(b.account.code)),
    })
  }
  buckets.sort((a, b) => {
    const tA = ACCOUNT_TYPE_META[a.account.code.replace('__type__', '')]?.order ?? 99
    const tB = ACCOUNT_TYPE_META[b.account.code.replace('__type__', '')]?.order ?? 99
    return tA - tB
  })

  if (groupNodes.length > 0) {
    buckets.push({
      account: { code: '__type__sap_groups', name: `SAP-Titelkonten (${groupNodes.length})`, isGroup: true, father: '', level: 0 },
      children: groupNodes,
    })
  }
  return buckets
}

function flattenMatchingNodes(nodes: TreeNode[], query: string): CoaAccount[] {
  const q = query.toLowerCase()
  const result: CoaAccount[] = []
  function walk(n: TreeNode) {
    if (n.account.code.toLowerCase().includes(q) || n.account.name.toLowerCase().includes(q))
      result.push(n.account)
    n.children.forEach(walk)
  }
  nodes.forEach(walk)
  return result
}

function hasAnyMovement(node: TreeNode): boolean {
  if (node.account.hasMovements) return true
  return node.children.some(hasAnyMovement)
}

function filterTreeByMovements(nodes: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = []
  for (const node of nodes) {
    if (!hasAnyMovement(node)) continue
    const filteredChildren = filterTreeByMovements(node.children)
    result.push({ account: node.account, children: filteredChildren })
  }
  return result
}

// GuV-relevante Kontotypen (Erloes + Aufwand) — Bilanzkonten (at_Other, Assets, Liabilities…) ausblenden
const GUV_TYPES = new Set([
  'at_Revenue', 'at_OtherRevenue', 'at_Revenues',
  'at_Expenditure', 'at_OtherExpenditure', 'at_Expenses',
])

function hasAnyGuvDescendant(node: TreeNode): boolean {
  if (!node.account.isGroup && node.children.length === 0) {
    return !node.account.accountType || GUV_TYPES.has(node.account.accountType)
  }
  return node.children.some(hasAnyGuvDescendant)
}

function filterTreeByGuv(nodes: TreeNode[]): TreeNode[] {
  return nodes
    .filter((n) => {
      if (n.account.code.startsWith('__type__')) {
        return GUV_TYPES.has(n.account.code.replace('__type__', ''))
      }
      if (!n.account.accountType) return true
      if (GUV_TYPES.has(n.account.accountType)) return true
      // Gruppen-Konten behalten, wenn sie GuV-Kinder haben (auch wenn eigener Typ Bilanz ist)
      if (n.account.isGroup || n.children.length > 0) return hasAnyGuvDescendant(n)
      return false
    })
    .map((n) => ({ ...n, children: filterTreeByGuv(n.children) }))
}

function hasAnyUnmapped(node: TreeNode, mapped: Map<string, string[]>): boolean {
  // Wenn diese Gruppe selbst als Gruppen-Mapping zugeordnet ist, gelten alle Kinder als gemappt
  if (mapped.has(node.account.code)) return false
  if (!node.account.isGroup && node.children.length === 0) return !mapped.has(node.account.code)
  return node.children.some((c) => hasAnyUnmapped(c, mapped))
}

function filterTreeByUnmapped(nodes: TreeNode[], mapped: Map<string, string[]>): TreeNode[] {
  const result: TreeNode[] = []
  for (const node of nodes) {
    if (!hasAnyUnmapped(node, mapped)) continue
    const filteredChildren = filterTreeByUnmapped(node.children, mapped)
    result.push({ account: node.account, children: filteredChildren })
  }
  return result
}

// ─── Auto-suggestion ─────────────────────────────────────────────────────────

// IMPORTANT: order matters — more specific entries must come BEFORE broader ones.
// e.g. 'kfz_steuer' before 'kfz_kosten', otherwise 'kfz' from kfz_kosten matches first.
const KEYWORD_MAP: Array<{ positionCode: string; keywords: string[] }> = [
  { positionCode: 'wareneinsatz',      keywords: ['waren', 'einkauf', 'material', 'roh', 'handelswaren', 'beschaffung', 'leistungen bezogen', 'fremdbezug', 'fremdleistung'] },
  { positionCode: 'sonstige_ertraege', keywords: ['sonstige ertrag', 'nebenertrag', 'sonstige leistung', 'erträge aus', 'sonstige betriebliche'] },
  { positionCode: 'personalkosten',    keywords: ['personal', 'lohn', 'löhn', 'gehalt', 'gehält', 'tantiem', 'krankengeld', 'lohnsteuer', 'kirchensteuer', 'steuern an', 'steuer an', 'arbeitgeber', 'urlaubsrückstellung', 'berufsgenossenschaft', 'arbeitnehmer', 'sozialversicherung'] },
  { positionCode: 'raumkosten',        keywords: ['miete', 'pacht', 'nebenkosten', 'heizung', 'strom', 'wasser', 'entsorgung', 'reinigung', 'grundsteuer'] },
  { positionCode: 'kfz_steuer',        keywords: ['kraftfahrzeugsteuer', 'kfz-steuer', 'kfz steuer', 'kraftf.steuer', 'kfz.-steuer'] },
  { positionCode: 'kfz_kosten',        keywords: ['kfz', 'kraftfahrzeug', 'fahrzeug', 'auto', 'leasing', 'benzin', 'treibstoff', 'kraftstoff', 'maut', 'pkw', 'lkw'] },
  { positionCode: 'werbe_reisekosten', keywords: ['werbung', 'marketing', 'reise', 'bewirtung', 'repräsentation', 'messe', 'werbematerial'] },
  { positionCode: 'reparaturen',       keywords: ['reparatur', 'instandhalt', 'instandsetzung', 'wartung', 'unterhalt', 'wartungsvertrag'] },
  { positionCode: 'versicherungen',    keywords: ['versicherung'] },
  { positionCode: 'sonstige_kosten',   keywords: ['bürobedarf', 'büromaterial', 'telefon', 'porto', 'it-kosten', 'software', 'beratung', 'rechtsanwalt', 'steuerberatung', 'sonstige kosten', 'übrige kosten', 'lizenzen', 'abonnement', 'beiträge', 'verwaltung'] },
  { positionCode: 'afa',               keywords: ['abschreibung', 'afa', 'wertminderung', 'amortis'] },
  { positionCode: 'zinsaufwand',       keywords: ['zinsaufwand', 'bankgebühr', 'bankkosten', 'zinsen', 'disagio', 'damnum', 'kontoführung', 'kreditgebühr'] },
  { positionCode: 'zinsertraege',      keywords: ['zinsertr', 'zinseinnahm', 'zinsertrag'] },
]

// SAP account-type based fallback when no keyword match.
// NOTE: at_Revenues covers BOTH Umsatzerloese and Sonstige Ertraege in SAP B1 —
// can't distinguish them by type alone, so only Aufwand gets a fallback.
const ACCOUNT_TYPE_FALLBACK: Array<{ types: string[]; positionCode: string }> = [
  { types: ['at_Expenditure', 'at_OtherExpenditure', 'at_Expenses'],    positionCode: 'sonstige_kosten' },
]

function suggestMappings(
  accounts: CoaAccount[],
  existing: AccountMappingRow[],
  glPositions: BwaPosition[],
): AccountMappingRow[] {
  const validCodes = new Set(glPositions.map((p) => p.code))
  // Accounts already mapped ANYWHERE should never be suggested again
  const alreadyMappedAccounts = new Set(existing.map((r) => r.sapAccount))
  const suggestions: AccountMappingRow[] = []

  for (const acct of accounts) {
    if (acct.isGroup) continue
    if (alreadyMappedAccounts.has(acct.code)) continue  // already mapped → skip
    // Never suggest balance sheet accounts for P&L positions
    if (acct.accountType && !GUV_TYPES.has(acct.accountType)) continue
    const name = acct.name.toLowerCase()

    // 1) Keyword match (precise, respect order in KEYWORD_MAP)
    let matched = false
    for (const { positionCode, keywords } of KEYWORD_MAP) {
      if (!validCodes.has(positionCode)) continue
      if (keywords.some((kw) => name.includes(kw))) {
        suggestions.push({ positionCode, sapAccount: acct.code, weight: 1, isGroupMapping: false })
        matched = true
        break
      }
    }
    if (matched) continue

    // 2) SAP-AccountType Fallback (nur wenn kein Keyword-Match)
    if (acct.accountType) {
      for (const { types, positionCode } of ACCOUNT_TYPE_FALLBACK) {
        if (!validCodes.has(positionCode)) continue
        if (types.includes(acct.accountType)) {
          suggestions.push({ positionCode, sapAccount: acct.code, weight: 1, isGroupMapping: false })
          break
        }
      }
    }
  }
  return suggestions
}

/**
 * Returns the best keyword match for a single account name.
 * Used to check existing mappings for plausibility.
 */
function getBestKeywordMatch(accountName: string, accountType: string | undefined): string | null {
  if (accountType && !GUV_TYPES.has(accountType)) return null
  const name = accountName.toLowerCase()
  for (const { positionCode, keywords } of KEYWORD_MAP) {
    if (keywords.some((kw) => name.includes(kw))) return positionCode
  }
  return null
}

// ─── Draggable Account-Node ──────────────────────────────────────────────────

function DraggableAccountNode({
  node,
  depth,
  expandedGroups,
  toggleGroup,
  mappedAccounts,
}: {
  node: TreeNode
  depth: number
  expandedGroups: Set<string>
  toggleGroup: (code: string) => void
  mappedAccounts: Map<string, string[]>
}) {
  const { account, children } = node
  const isGroup = account.isGroup || children.length > 0
  const isExpanded = expandedGroups.has(account.code)
  const mapped = mappedAccounts.get(account.code) ?? []
  const indent = depth * 14

  const isVirtual = account.code.startsWith('__type__')

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: account.code,
    data: { account },
    disabled: isVirtual,
  })

  const leafCount = useMemo(() => {
    function count(n: TreeNode): number {
      return n.children.length === 0 ? 1 : n.children.reduce((s, c) => s + count(c), 0)
    }
    return count(node)
  }, [node])

  const movedLeafCount = useMemo(() => {
    function count(n: TreeNode): number {
      if (n.children.length === 0) return n.account.hasMovements ? 1 : 0
      return n.children.reduce((s, c) => s + count(c), 0)
    }
    return count(node)
  }, [node])

  return (
    <div>
      <div
        ref={isVirtual ? undefined : setNodeRef}
        {...(isVirtual ? {} : listeners)}
        {...(isVirtual ? {} : attributes)}
        className={`flex items-center gap-1 px-1 py-0.5 rounded group transition-colors ${
          isVirtual ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
        } ${isDragging ? 'opacity-40' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${6 + indent}px` }}
        title={isVirtual ? undefined : isGroup ? 'Gruppe per Drag zuordnen' : 'Konto per Drag zuordnen'}
      >
        {isGroup ? (
          <button
            className="flex items-center gap-1 flex-1 min-w-0 text-left"
            onClick={(e) => { e.stopPropagation(); toggleGroup(account.code) }}
          >
            <span className="shrink-0 text-gray-400">
              {isExpanded
                ? <ChevronDown className="size-3.5" />
                : <ChevronRight className="size-3.5" />}
            </span>
            <Layers className="size-3 shrink-0 text-indigo-400" />
            {!isVirtual && <span className="font-mono text-caption text-indigo-700 shrink-0">{account.code}</span>}
            <span className={`text-caption truncate flex-1 ml-0.5 ${isVirtual ? 'font-medium text-gray-700' : 'text-gray-600'}`}>{account.name}</span>
            {movedLeafCount > 0 && (
              <span className="text-caption text-emerald-600 font-medium shrink-0 ml-1" title={`${movedLeafCount} von ${leafCount} Konten bebuchtet`}>
                {movedLeafCount}/{leafCount}
              </span>
            )}
            {movedLeafCount === 0 && (
              <span className="text-caption text-gray-400 shrink-0 ml-1">{leafCount}</span>
            )}
          </button>
        ) : (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <span className="w-3.5 shrink-0" />
            <span className="font-mono text-caption text-gray-600 shrink-0 w-14">{account.code}</span>
            <span className="text-caption text-gray-700 truncate flex-1">{account.name}</span>
            {account.hasMovements
              ? <span className="text-caption bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 rounded shrink-0" title="Hat Buchungsbewegungen">bebuchtet</span>
              : <span className="text-caption text-gray-300 shrink-0">–</span>
            }
          </div>
        )}
        {mapped.length > 0 && (
          <div className="flex gap-0.5 shrink-0 ml-1" title={mapped.length > 1 ? `⚠ Doppelt zugeordnet: ${mapped.join(', ')}` : mapped[0]}>
            {mapped.slice(0, 2).map((pos) => (
              <span key={pos} className={`text-caption px-1 rounded ${mapped.length > 1 ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-primary/10 text-primary'}`}>
                {pos.replace(/_/g, ' ').slice(0, 10)}
              </span>
            ))}
            {mapped.length > 2 && <span className="text-caption text-red-600">+{mapped.length - 2}</span>}
          </div>
        )}
        {/* Drag-Handle: fuer Gruppen immer sichtbar (indigo), fuer Blattkonten nur hover */}
        <span
          className={`shrink-0 ml-1 select-none text-caption ${isGroup && !isVirtual ? 'text-indigo-400' : 'opacity-0 group-hover:opacity-100 text-gray-400'}`}
          title={isGroup && !isVirtual ? `Gruppe ziehen → alle ${leafCount} Unterkonten werden mit zugeordnet (isGroupMapping)` : undefined}
        >⠿</span>
      </div>
      {isGroup && isExpanded && children.map((child) => (
        <DraggableAccountNode
          key={child.account.code}
          node={child}
          depth={depth + 1}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          mappedAccounts={mappedAccounts}
        />
      ))}
    </div>
  )
}

// ─── Droppable Position-Zone ─────────────────────────────────────────────────

function DroppablePosition({
  position,
  rows,
  coa,
  onRemove,
  onWeightChange,
}: {
  position: BwaPosition
  rows: AccountMappingRow[]
  coa: CoaAccount[]
  onRemove: (sapAccount: string) => void
  onWeightChange: (sapAccount: string, weight: number) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: position.code })
  const [collapsed, setCollapsed] = useState(true)

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border transition-colors ${
        isOver ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 bg-white'
      }`}
    >
      <button
        className="w-full flex items-center gap-2 px-3 py-2 border-b border-gray-100 text-left hover:bg-gray-50 rounded-t-lg"
        onClick={() => setCollapsed((v) => !v)}
      >
        <span className="shrink-0 text-gray-400">
          {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </span>
        <span className="text-small font-medium text-gray-800 flex-1">{position.label}</span>
        {position.sourceKind !== 'gl' && (
          <span className="text-caption text-gray-400 bg-gray-100 px-1.5 rounded shrink-0" title="PLAN-Datenquelle">{position.sourceKind}</span>
        )}
        <span className="text-caption text-gray-400">{rows.length > 0 ? `${rows.length} Konto${rows.length > 1 ? 'en' : ''}` : ''}</span>
      </button>
      {!collapsed && (
        <div className="px-2 py-1.5 space-y-0.5 min-h-[32px]">
          {rows.map((row) => {
            const acct = coa.find((a) => a.code === row.sapAccount)
            const suggestedPos = acct ? getBestKeywordMatch(acct.name, acct.accountType) : null
            const mismatch = suggestedPos !== null && suggestedPos !== position.code
            return (
              <div key={row.sapAccount} className="flex items-center gap-1.5 group">
                {row.isGroupMapping && <Layers className="size-3 text-indigo-400 shrink-0" />}
                {mismatch && (
                  <span title={`Keyword-Analyse empfiehlt: ${suggestedPos}`}>
                    <AlertTriangle className="size-3 text-amber-400 shrink-0" />
                  </span>
                )}
                <span className="font-mono text-caption text-gray-500 w-16 shrink-0">{row.sapAccount}</span>
                <span className="text-caption text-gray-700 flex-1 truncate">{acct?.name ?? ''}</span>
                {acct?.hasMovements
                  ? <span className="text-caption bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 rounded shrink-0" title="Hat Buchungsbewegungen im Cache-Zeitraum">bebuchtet</span>
                  : <span className="text-caption text-gray-300 shrink-0" title="Keine Buchungsbewegungen">–</span>
                }
                <Input
                  type="number"
                  step="0.1"
                  value={row.weight}
                  onChange={(e) => onWeightChange(row.sapAccount, Number(e.target.value))}
                  className="w-14 text-right text-caption h-5 px-1"
                />
                <IconButton
                  icon={<Trash2 className="size-3" />}
                  aria-label="Entfernen"
                  variant="ghost"
                  size="xs"
                  className="opacity-0 group-hover:opacity-100"
                  onClick={() => onRemove(row.sapAccount)}
                />
              </div>
            )
          })}
          {isOver && rows.length === 0 && (
            <div className="text-caption text-primary text-center py-1">Hier ablegen</div>
          )}
          {!isOver && rows.length === 0 && (
            <div className="text-caption text-gray-300 text-center py-0.5">Konto hierher ziehen…</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── BWA-Positions-Baum (rechte Spalte) ──────────────────────────────────────

function BwaPositionNode({
  position,
  allPositions,
  rows,
  coa,
  expandedPositions,
  togglePosition,
  onRemove,
  onWeightChange,
  depth = 0,
}: {
  position: BwaPosition
  allPositions: BwaPosition[]
  rows: AccountMappingRow[]
  coa: CoaAccount[]
  expandedPositions: Set<string>
  togglePosition: (code: string) => void
  onRemove: (posCode: string, sapAccount: string) => void
  onWeightChange: (posCode: string, sapAccount: string, weight: number) => void
  depth?: number
}) {
  const children = allPositions.filter((p) => p.parentCode === position.code).sort((a, b) => a.sortOrder - b.sortOrder)
  const hasChildren = children.length > 0
  const isExpanded = expandedPositions.has(position.code)
  const indent = depth * 12

  // Alle Blatt-Positionen (keine Aggregate, kein computed-Only) sind droppable.
  // sourceKind bestimmt die PLAN-Datenquelle; GL-Mappings werden fuer IST genutzt.
  if (!position.isAggregate && !hasChildren && position.sign !== 'sum') {
    const posRows = rows.filter((r) => r.positionCode === position.code && r.sapAccount)
    return (
      <div style={{ marginLeft: indent }}>
        <DroppablePosition
          position={position}
          rows={posRows}
          coa={coa}
          onRemove={(sap) => onRemove(position.code, sap)}
          onWeightChange={(sap, w) => onWeightChange(position.code, sap, w)}
        />
      </div>
    )
  }

  if (position.sign === 'sum' && !hasChildren) {
    return (
      <div style={{ marginLeft: indent }} className="px-3 py-1.5 flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-lg">
        <span className="text-small font-semibold text-gray-800 flex-1">{position.label}</span>
        <span className="text-caption text-gray-400">∑</span>
      </div>
    )
  }

  if (hasChildren) {
    return (
      <div style={{ marginLeft: indent }}>
        <button
          className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left hover:bg-gray-50 rounded-lg border border-gray-200 bg-white"
          onClick={() => togglePosition(position.code)}
        >
          <span className="shrink-0 text-gray-400">
            {isExpanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </span>
          <span className="text-small font-medium text-gray-700 flex-1">{position.label}</span>
          <span className="text-caption text-gray-400">{position.sourceKind}</span>
        </button>
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-2">
            {children.map((child) => (
              <BwaPositionNode
                key={child.code}
                position={child}
                allPositions={allPositions}
                rows={rows}
                coa={coa}
                expandedPositions={expandedPositions}
                togglePosition={togglePosition}
                onRemove={onRemove}
                onWeightChange={onWeightChange}
                depth={0}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

}

// ─── AccountBrowser ───────────────────────────────────────────────────────────

function AccountBrowser({
  coa,
  companyKey,
  mappedAccounts,
  onCoaRefreshed,
  debugSample,
}: {
  coa: CoaAccount[]
  companyKey: string
  mappedAccounts: Map<string, string[]>
  onCoaRefreshed: (accounts: CoaAccount[], debug?: unknown[]) => void
  debugSample?: Array<{ code: string; father: string; level: number; isGroup: boolean }>
}) {
  // AccountType-Verteilung aus dem aktuellen `coa`-Array ableiten — kein
  // separater Fetch noetig, immer aktuell.
  const accountTypeStats = useMemo(() => {
    const stats: Record<string, number> = {}
    for (const a of coa) {
      const key = a.accountType ?? '(none)'
      stats[key] = (stats[key] ?? 0) + 1
    }
    return stats
  }, [coa])
  const [search, setSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [refreshInfo, setRefreshInfo] = useState<string | null>(null)
  const [showDebug, setShowDebug] = useState(false)
  const [onlyWithMovements, setOnlyWithMovements] = useState(false)
  const [onlyUnmapped, setOnlyUnmapped] = useState(false)
  const [onlyGuv, setOnlyGuv] = useState(true)
  const [sortMode, setSortMode] = useState<'code' | 'name' | 'bebuchtet'>('code')

  const { nodes: rawTree, usedLevelFallback } = useMemo(() => buildTree(coa), [coa])
  const tree = useMemo(() => {
    let nodes = rawTree
    if (onlyGuv) nodes = filterTreeByGuv(nodes)
    if (onlyWithMovements) nodes = filterTreeByMovements(nodes)
    if (onlyUnmapped) nodes = filterTreeByUnmapped(nodes, mappedAccounts)
    if (sortMode !== 'code') {
      function sortNodes(ns: TreeNode[]): TreeNode[] {
        return ns.map((n) => ({ ...n, children: sortNodes(n.children) })).sort((a, b) => {
          if (sortMode === 'name') return a.account.name.localeCompare(b.account.name, 'de')
          // bebuchtet: bebuchte zuerst
          const aM = a.account.hasMovements ? 0 : 1
          const bM = b.account.hasMovements ? 0 : 1
          return aM !== bM ? aM - bM : a.account.code.localeCompare(b.account.code)
        })
      }
      nodes = sortNodes(nodes)
    }
    return nodes
  }, [rawTree, onlyWithMovements, onlyUnmapped, mappedAccounts, sortMode])

  function toggleGroup(code: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  async function handleRefreshCoa() {
    setRefreshing(true)
    setRefreshError(null)
    setRefreshInfo(null)
    try {
      const postRes = await apiClient.post<{ ok: boolean; accountCount: number; sapTotalCount?: number | null }>(
        `/liquidity/refresh-coa/${companyKey}`, {},
      )
      const count = postRes.data.accountCount ?? 0
      const sapTotal = postRes.data.sapTotalCount
      const res = await apiClient.get<{ accounts: CoaAccount[]; debugSample?: unknown[]; hint?: string }>(
        `/liquidity-planning/coa?company=${companyKey}`,
      )
      onCoaRefreshed(res.data.accounts, res.data.debugSample)
      const totalInfo = sapTotal != null ? ` (SAP meldet ${sapTotal} gesamt)` : ''
      setRefreshInfo(`${count} Konten aus SAP geladen (${res.data.accounts.length} im Browser)${totalInfo}`)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
      const detail = axiosErr?.response?.data?.error ?? axiosErr?.response?.data?.message ?? axiosErr?.message
      setRefreshError(detail ?? 'Fehler beim Laden der Konten')
    } finally {
      setRefreshing(false)
    }
  }

  const isFallbackMode = coa.length > 0 && coa.every((a) => !a.name) && coa.every((a) => !a.isGroup)

  // Suche laeuft auf dem RAW-Tree (nicht auf dem gefilterten), damit der
  // User auch Konten finden kann, die durch GuV-/bebucht-/Mapping-Filter
  // ausgeblendet sind. Beispiel: User sucht "59230" mit "nur GuV"-Filter,
  // das Konto hat aber AccountType=at_Other → wuerde sonst nie gefunden.
  const flatMatches = search ? flattenMatchingNodes(rawTree, search) : null

  if (coa.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 text-center">
        <p className="text-small text-gray-500">Noch keine Konten geladen.</p>
        <Button variant="secondary" size="sm" onClick={handleRefreshCoa} disabled={refreshing}>
          <RefreshCw className={`size-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Lade Konten…' : 'Konten aus SAP laden'}
        </Button>
        {refreshError && <p className="text-caption text-red-600">{refreshError}</p>}
        {refreshInfo && <p className="text-caption text-green-700">{refreshInfo}</p>}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-2 py-1.5 border-b border-gray-100 flex gap-1.5 items-center">
        <Input
          placeholder="Konto suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-7 text-caption"
        />
        <label className="flex items-center gap-1 cursor-pointer shrink-0 text-caption text-gray-500 select-none" title="Nur Konten mit Buchungsbewegungen anzeigen">
          <input
            type="checkbox"
            checked={onlyWithMovements}
            onChange={(e) => setOnlyWithMovements(e.target.checked)}
            className="size-3 accent-primary"
          />
          <span>bebuchte</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer shrink-0 text-caption text-gray-500 select-none" title="Nur noch nicht zugeordnete Konten anzeigen">
          <input
            type="checkbox"
            checked={onlyUnmapped}
            onChange={(e) => setOnlyUnmapped(e.target.checked)}
            className="size-3 accent-primary"
          />
          <span>ohne Mapping</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer shrink-0 text-caption text-gray-500 select-none" title="Nur GuV-relevante Konten (Erlös/Aufwand) — Bilanzkonten ausblenden">
          <input
            type="checkbox"
            checked={onlyGuv}
            onChange={(e) => setOnlyGuv(e.target.checked)}
            className="size-3 accent-primary"
          />
          <span>nur GuV</span>
        </label>
        <div className="flex items-center gap-0.5 shrink-0" title="Sortierung">
          <ArrowUpDown className="size-3 text-gray-400" />
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
            className="text-caption text-gray-500 bg-transparent border-none outline-none cursor-pointer"
          >
            <option value="code">Nr.</option>
            <option value="name">A–Z</option>
            <option value="bebuchtet">bebuchtet</option>
          </select>
        </div>
        <Button variant="ghost" size="xs" onClick={handleRefreshCoa} disabled={refreshing} title="Kontenplan neu laden">
          <RefreshCw className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isFallbackMode && (
        <div className="px-2 py-1.5 text-caption text-orange-800 bg-orange-50 border-b border-orange-200 flex items-center gap-1.5">
          <span className="shrink-0">⚠</span>
          <span className="flex-1">Kontenhierarchie fehlt — bitte Konten aus SAP neu laden (⟳), um Namen und Gruppen zu sehen.</span>
        </div>
      )}

      {usedLevelFallback && !isFallbackMode && (
        <div className="px-2 py-1 text-caption text-amber-700 bg-amber-50 border-b border-amber-100">
          Hierarchie aus Level-Feld aufgebaut (SAP liefert keine Father-Codes).
          <button className="ml-1 underline" onClick={() => setShowDebug((v) => !v)}>Details</button>
        </div>
      )}

      {debugSample && !usedLevelFallback && (
        <div className="px-2 py-1 border-b border-gray-100 text-caption text-gray-400">
          <button className="underline" onClick={() => setShowDebug((v) => !v)}>
            {showDebug ? 'Debug ausblenden' : 'Debug-Info (Father-Felder)'}
          </button>
        </div>
      )}

      {showDebug && debugSample && (
        <div className="px-2 py-2 bg-gray-50 border-b border-gray-200 text-caption font-mono space-y-0.5 overflow-x-auto">
          {/* Such-Diagnose: zeigt fuer den aktuell gesuchten Code direkt
              den Status — gefunden/nicht gefunden, AccountType, isGroup,
              hasMovements. Spart das Scrollen durch die Liste. */}
          {search && (() => {
            const q = search.trim()
            const exact = coa.find((a) => a.code === q)
            const partial = coa.filter((a) => a.code.includes(q) && a.code !== q).slice(0, 3)
            return (
              <div className="mb-2 p-1.5 bg-white border border-amber-200 rounded">
                <p className="text-amber-700 mb-0.5">Such-Diagnose „{q}":</p>
                {exact ? (
                  <div className="text-gray-700">
                    <span className="font-bold">{exact.code}</span>
                    {' · '}{exact.name || '(leer)'}
                    {' · '}AccountType=<span className="text-blue-600">{exact.accountType ?? '(none)'}</span>
                    {' · '}{exact.isGroup ? 'Gruppe' : 'Konto'}
                    {' · '}{exact.hasMovements ? 'bebuchtet' : 'keine Bewegungen'}
                    {exact.father && <> · father={exact.father}</>}
                    {exact.level !== undefined && <> · level={exact.level}</>}
                  </div>
                ) : (
                  <div className="text-red-600">Code „{q}" NICHT im SAP-Cache gefunden.</div>
                )}
                {partial.length > 0 && (
                  <p className="text-gray-500 mt-0.5">Aehnliche Codes: {partial.map((a) => a.code).join(', ')}</p>
                )}
              </div>
            )
          })()}
          {accountTypeStats && (
            <div className="mb-2">
              <p className="text-gray-500 mb-0.5">AccountType-Verteilung im Cache ({coa.length} Konten):</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-gray-700">
                {Object.entries(accountTypeStats)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, n]) => (
                    <div key={type}>
                      <span className="text-blue-600">{type}</span>
                      {': '}{n}
                    </div>
                  ))}
              </div>
            </div>
          )}
          <p className="text-gray-500 mb-1">5 Gruppen + 5 Buchungskonten (code / father / level / isGroup):</p>
          {debugSample.map((s, i) => (
            <div key={i} className="text-gray-600">
              <span className={s.isGroup ? 'text-indigo-600' : ''}>{s.code}</span>
              {' / '}
              <span className={s.father ? 'text-green-700' : 'text-red-600'}>{s.father || '(leer!)'}</span>
              {' / '}{s.level}{' / '}{s.isGroup ? 'Gruppe' : 'Konto'}
            </div>
          ))}
          <p className="text-gray-400 mt-1">Buchungskonten ohne father-Code erscheinen als Wurzel-Einträge.</p>
        </div>
      )}

      {refreshError && <p className="px-2 py-1 text-caption text-red-600 border-b border-red-100">{refreshError}</p>}
      {refreshInfo && <p className="px-2 py-1 text-caption text-green-700 border-b border-green-100">{refreshInfo}</p>}

      <div className="flex-1 overflow-y-auto px-1 py-1">
        {flatMatches ? (
          flatMatches.map((acct) => (
            <DraggableAccountNode
              key={acct.code}
              node={{ account: acct, children: [] }}
              depth={0}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              mappedAccounts={mappedAccounts}
            />
          ))
        ) : (
          tree.map((node) => (
            <DraggableAccountNode
              key={node.account.code}
              node={node}
              depth={0}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              mappedAccounts={mappedAccounts}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Vorschlag-Dialog ─────────────────────────────────────────────────────────

function SuggestionPreview({
  suggestions,
  coa,
  positions,
  onApply,
  onClose,
}: {
  suggestions: AccountMappingRow[]
  coa: CoaAccount[]
  positions: BwaPosition[]
  onApply: (rows: AccountMappingRow[]) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(suggestions.map((s) => `${s.positionCode}:${s.sapAccount}`)),
  )

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function handleApply() {
    onApply(suggestions.filter((s) => selected.has(`${s.positionCode}:${s.sapAccount}`)))
    onClose()
  }

  const byPosition = positions
    .filter((p) => suggestions.some((s) => s.positionCode === p.code))
    .map((p) => ({ position: p, rows: suggestions.filter((s) => s.positionCode === p.code) }))

  return (
    <div className="fixed inset-0 z-modal bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <Sparkles className="size-5 text-primary" />
          <div className="flex-1">
            <h2 className="text-h5 text-gray-900">Zuordnungsvorschlag</h2>
            <p className="text-caption text-gray-500">{suggestions.length} Konten wurden erkannt — wähle aus, welche übernommen werden sollen.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {byPosition.map(({ position, rows }) => (
            <div key={position.code}>
              <p className="text-small font-medium text-gray-700 mb-1">{position.label}</p>
              <div className="space-y-0.5">
                {rows.map((row) => {
                  const key = `${row.positionCode}:${row.sapAccount}`
                  const acct = coa.find((a) => a.code === row.sapAccount)
                  return (
                    <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                        className="accent-primary"
                      />
                      {row.isGroupMapping && <Layers className="size-3 text-indigo-400" />}
                      <span className="font-mono text-caption text-gray-500 w-16">{row.sapAccount}</span>
                      <span className="text-caption text-gray-700 flex-1">{acct?.name ?? row.sapAccount}</span>
                      {acct?.hasMovements && (
                        <span className="text-caption bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 rounded" title="Hat Buchungsbewegungen">bebuchtet</span>
                      )}
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
          {suggestions.length === 0 && (
            <p className="text-small text-gray-500 text-center py-8">Keine Übereinstimmungen gefunden.</p>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleApply} disabled={selected.size === 0}>
            {selected.size} Zuordnung{selected.size !== 1 ? 'en' : ''} übernehmen
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── MappingTab ───────────────────────────────────────────────────────────────

export function MappingTab({
  companyKey, positions, mapping, coa: coaProp, onSaved,
}: {
  companyKey: string
  positions: BwaPosition[]
  mapping: AccountMappingRow[]
  coa: CoaAccount[]
  onSaved: () => void
}) {
  const [rows, setRows] = useState<AccountMappingRow[]>(mapping)
  const [coa, setCoa] = useState<CoaAccount[]>(coaProp)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [debugSample, setDebugSample] = useState<Array<{ code: string; father: string; level: number; isGroup: boolean }> | undefined>()
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set(['umsatzerloese']))

  function togglePosition(code: string) {
    setExpandedPositions((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  useEffect(() => { setRows(mapping) }, [mapping])
  useEffect(() => { setCoa(coaProp) }, [coaProp])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  // Alle Blatt-Positionen (nicht nur sourceKind='gl') koennen SAP-Konten erhalten.
  // sourceKind bestimmt die PLAN-Datenquelle; GL-Mappings liefern IST-Werte fuer jede Position.
  const glPositions = positions.filter((p) => !p.isAggregate && p.sign !== 'sum')

  const mappedAccounts = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const r of rows) {
      if (!r.sapAccount) continue
      if (!map.has(r.sapAccount)) map.set(r.sapAccount, [])
      map.get(r.sapAccount)!.push(r.positionCode)
    }
    return map
  }, [rows])

  function addMapping(positionCode: string, sapAccount: string, isGroupMapping = false, move = false) {
    const alreadyHere = rows.some((r) => r.sapAccount === sapAccount && r.positionCode === positionCode)
    if (alreadyHere) return
    setRows((prev) => {
      // Beim Verschieben (Drag): aus allen anderen Positionen entfernen → kein Doppel-Zaehlen
      const base = move ? prev.filter((r) => r.sapAccount !== sapAccount) : prev
      return [...base, { positionCode, sapAccount, weight: 1, isGroupMapping }]
    })
  }

  function removeMapping(positionCode: string, sapAccount: string) {
    setRows((prev) => prev.filter((r) => !(r.positionCode === positionCode && r.sapAccount === sapAccount)))
  }

  function updateWeight(positionCode: string, sapAccount: string, weight: number) {
    setRows((prev) => prev.map((r) =>
      r.positionCode === positionCode && r.sapAccount === sapAccount ? { ...r, weight } : r,
    ))
  }

  function addManualRow() {
    setRows((prev) => [...prev, { positionCode: glPositions[0]?.code ?? '', sapAccount: '', weight: 1 }])
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    const { active, over } = event
    if (!over) return
    const sapAccount = active.id as string
    if (sapAccount.startsWith('__type__')) return  // virtual bucket nodes are not mappable
    const positionCode = over.id as string
    if (!glPositions.some((p) => p.code === positionCode)) return
    const account = coa.find((a) => a.code === sapAccount)
    addMapping(positionCode, sapAccount, account?.isGroup ?? false, true)  // move=true: aus alten Positionen entfernen
  }

  const suggestions = useMemo(
    () => suggestMappings(coa, rows, glPositions),
    [coa, rows, glPositions],
  )

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const valid = rows.filter((r) => r.positionCode && r.sapAccount)
      await apiClient.put(`/liquidity-planning/mapping?company=${companyKey}`, {
        entries: valid.map((r) => ({
          positionCode: r.positionCode,
          sapAccount: r.sapAccount,
          weight: r.weight,
          isGroupMapping: r.isGroupMapping ?? false,
        })),
      })
      setSavedAt(new Date().toLocaleTimeString('de-DE'))
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    } finally {
      setSaving(false)
    }
  }

  const activeAccount = activeId ? coa.find((a) => a.code === activeId) : null
  const leafCount = coa.filter((a) => !a.isGroup).length
  const groupCount = coa.filter((a) => a.isGroup).length

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-h5">SAP-Konten → BWA-Positionen</span>
          <div className="flex gap-2 flex-wrap">
            {coa.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSuggestions(true)}
                disabled={suggestions.length === 0}
                title={suggestions.length === 0 ? 'Keine neuen Vorschläge (alle Treffer bereits zugeordnet)' : undefined}
              >
                <Sparkles className="size-4 mr-1.5 text-amber-500" />
                Vorschlag {suggestions.length > 0 ? `(${suggestions.length})` : '(0)'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={addManualRow}>
              <Plus className="size-4 mr-1" /> Manuell
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              <Save className="size-4 mr-1" /> {saving ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        </div>

        {savedAt && <p className="text-caption text-green-700">Gespeichert {savedAt}</p>}
        {error && <p className="text-caption text-red-700">{error}</p>}

        <div className="grid grid-cols-5 gap-4" style={{ height: 'calc(100vh - 280px)', minHeight: 400 }}>
          {/* Linke Spalte: SAP-Kontenbaum */}
          <Card className="col-span-2 flex flex-col overflow-hidden">
            <Card.Header className="py-2">
              <span className="text-small font-medium text-gray-700">SAP-Kontenbaum</span>
              {coa.length > 0 && (
                <span className="text-caption text-gray-400 ml-2">
                  {groupCount} Gruppen · {leafCount} Konten
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-0 flex-1 overflow-hidden">
              <AccountBrowser
                coa={coa}
                companyKey={companyKey}
                mappedAccounts={mappedAccounts}
                debugSample={debugSample}
                onCoaRefreshed={(accounts, debug) => {
                  setCoa(accounts)
                  setDebugSample(debug as typeof debugSample)
                }}
              />
            </Card.Body>
            <Card.Footer>
              <p className="text-caption text-gray-400">
                <span className="text-blue-500">●</span> Buchungsbewegungen ·{' '}
                <Layers className="size-3 text-indigo-400 inline" /> Gruppe ·{' '}
                <span className="text-gray-400">⠿ = ziehen zum Zuordnen</span>
              </p>
            </Card.Footer>
          </Card>

          {/* Rechte Spalte: BWA-Positionen als Baum (wie Plan-Matrix) */}
          <div className="col-span-3 flex flex-col gap-1 overflow-y-auto pr-0.5">
            {positions
              .filter((p) => p.section === 'guv' && p.parentCode === null)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((pos) => (
                <BwaPositionNode
                  key={pos.code}
                  position={pos}
                  allPositions={positions}
                  rows={rows}
                  coa={coa}
                  expandedPositions={expandedPositions}
                  togglePosition={togglePosition}
                  onRemove={removeMapping}
                  onWeightChange={updateWeight}
                />
              ))}

            {/* Manuelle Eintraege ohne Position */}
            {rows.filter((r) => !r.positionCode || !r.sapAccount).length > 0 && (
              <Card>
                <Card.Header className="py-2">
                  <span className="text-small font-medium text-gray-500">Unvollständige Einträge</span>
                </Card.Header>
                <Card.Body className="p-1">
                  <SimpleTable>
                    <SimpleTable.Body>
                      {rows.map((row, i) => {
                        if (row.positionCode && row.sapAccount) return null
                        return (
                          <SimpleTable.Tr key={i}>
                            <SimpleTable.Td>
                              <SelectRoot value={row.positionCode} onValueChange={(v) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, positionCode: v } : r))}>
                                <SelectTrigger className="min-w-[160px]">
                                  <SelectValue placeholder="Position" />
                                </SelectTrigger>
                                <SelectContent>
                                  {glPositions.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                                </SelectContent>
                              </SelectRoot>
                            </SimpleTable.Td>
                            <SimpleTable.Td>
                              <Input
                                value={row.sapAccount}
                                onChange={(e) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, sapAccount: e.target.value } : r))}
                                placeholder="Konto-Nr."
                                className="w-24 font-mono"
                              />
                            </SimpleTable.Td>
                            <SimpleTable.Td>
                              <IconButton icon={<Trash2 className="size-4" />} aria-label="Entfernen" variant="ghost" size="sm" onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))} />
                            </SimpleTable.Td>
                          </SimpleTable.Tr>
                        )
                      })}
                    </SimpleTable.Body>
                  </SimpleTable>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>

        {coa.length === 0 && (
          <p className="text-caption text-amber-700 bg-amber-50 px-3 py-2 rounded border border-amber-200">
            Hinweis: Kein SAP-Cache vorhanden. Liquiditäts-Dashboard zuerst aktualisieren.
          </p>
        )}
      </div>

      <DragOverlay>
        {activeAccount && (
          <div className="bg-white border border-primary shadow-lg rounded px-3 py-1.5 flex items-center gap-2 text-small">
            {activeAccount.isGroup && <Layers className="size-3.5 text-indigo-400" />}
            <span className="font-mono text-gray-500">{activeAccount.code}</span>
            <span className="text-gray-700">{activeAccount.name}</span>
          </div>
        )}
      </DragOverlay>

      {showSuggestions && (
        <SuggestionPreview
          suggestions={suggestions}
          coa={coa}
          positions={positions}
          onApply={(newRows) => setRows((prev) => {
            const result = [...prev]
            for (const r of newRows) {
              if (!result.some((x) => x.positionCode === r.positionCode && x.sapAccount === r.sapAccount))
                result.push(r)
            }
            return result
          })}
          onClose={() => setShowSuggestions(false)}
        />
      )}
    </DndContext>
  )
}
